import { GoogleMap, Marker, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { formatRefDate } from '../../utils/formatRefDate';
import { positionReports, vesselStatus } from './monitoringData';

const defaultCenter = { lat: -2.5489, lng: 118.0149 };

const Maps = ({ dataSource, selectedKey, onSelect, mapType }) => {

    const data = useMemo(() => positionReports(dataSource), [dataSource]);
    // console.log('data:', data);

    const mapRef = useRef(null);
    const [popupKey, setPopupKey] = useState('');
    const selectedPoint = data.find(point => point.key === popupKey);
    const selectPoint = key => { setPopupKey(key); onSelect(key); };

    useEffect(() => { setPopupKey(selectedKey); }, [selectedKey]);
    useEffect(() => { mapRef.current?.setMapTypeId(mapType === 'satellite' ? 'satellite' : 'hybrid'); }, [mapType]);

    // Calculate center coordinates for display (average of all coordinates)
    const center = useMemo(() => {
        if (data.length === 0) return defaultCenter;

        const totalLat = data.reduce((sum, point) => sum + point.lat, 0);
        const totalLng = data.reduce((sum, point) => sum + point.lng, 0);

        return {
            lat: totalLat / data.length,
            lng: totalLng / data.length
        };
    }, [data]);
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: "AIzaSyDgCJRdIE3hpeL8oR9IP5LQQrzn6Olb-Ow",
        preventGoogleFontsLoading: true
    });

    const handleMapLoad = (map) => {
        mapRef.current = map;
    };

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !window.google) return;
        if (!data.length) {
            map.setCenter(defaultCenter);
            map.setZoom(5);
            return;
        }
        const bounds = new window.google.maps.LatLngBounds();
        data.forEach(point => bounds.extend({ lat: point.lat, lng: point.lng }));
        map.fitBounds(bounds, { top: 72, right: 48, bottom: 70, left: 48 });
    }, [data]);

    if (loadError) {
        return (
            <div className="monitoring-map-state is-error" role="alert">
                <strong>Failed to load Google Maps</strong>
                <span>Please check your connection or browser content blocker.</span>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="monitoring-map-state is-loading" role="status" aria-label="Loading vessel map">
                <span className="monitoring-map-state__line" /><span className="monitoring-map-state__line" /><span>Loading vessel map...</span>
            </div>
        );
    }

    return (
        <div className="monitoring-map-canvas">
                <GoogleMap
                    options={{
                        mapTypeId: mapType === 'satellite' ? 'satellite' : 'hybrid',
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                        clickableIcons: false,
                    }}
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={center}
                    zoom={data.length > 0 ? 5 : 4}
                    onLoad={handleMapLoad}
                >
                    {data.length > 0 ? (
                        data.map((point, index) => (
                            <Fragment key={point.key}>
                                <Marker
                                    key={`marker-${index}`}
                                    position={{ lat: point.lat, lng: point.lng }}
                                    icon={{
                                        url: "/assets/ship-z.png",
                                        scaledSize: new window.google.maps.Size(selectedKey === point.key ? 72 : 58, selectedKey === point.key ? 72 : 58),
                                        anchor: new window.google.maps.Point(selectedKey === point.key ? 36 : 29, selectedKey === point.key ? 36 : 29)
                                    }}
                                    title={point.ShipName || "Vessel Position"}
                                    zIndex={selectedKey === point.key ? 10 : 1}
                                    onClick={() => selectPoint(point.key)}
                                />
                                <OverlayView
                                    key={`label-${index}`}
                                    position={{ lat: point.lat, lng: point.lng }}
                                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                >
                                    <button type="button" className={`monitoring-map-label ${selectedKey === point.key ? 'is-selected' : ''}`}
                                        onClick={() => selectPoint(point.key)} aria-label={`Select ${point.ShipName || point.ShipCode}`}>
                                        {point.ShipName || point.ShipCode || "Vessel Position"}
                                    </button>
                                </OverlayView>
                            </Fragment>
                        ))
                    ) : null}
                    {selectedPoint && <OverlayView position={{ lat: selectedPoint.lat, lng: selectedPoint.lng }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={() => ({ x: selectedPoint.lng > center.lng ? -200 : 18, y: -130 })}>
                        <div className="monitoring-map-popup" role="group" aria-label={`Position report for ${selectedPoint.ShipName || selectedPoint.ShipCode}`}>
                            <button className="monitoring-map-popup__close" type="button" aria-label="Close position popup" onClick={() => setPopupKey('')}>×</button>
                            <strong>{selectedPoint.ShipName || selectedPoint.ShipCode || 'Vessel'}</strong>
                            <em className={vesselStatus(selectedPoint) ? 'is-online' : 'is-offline'}>{vesselStatus(selectedPoint) ? 'Online' : 'Offline'}</em>
                            <span>Lat&nbsp; {selectedPoint.lat.toFixed(6)}</span>
                            <span>Lon&nbsp; {selectedPoint.lng.toFixed(6)}</span>
                            <small>{formatRefDate(selectedPoint.reportDate)}</small>
                            <button type="button" className="monitoring-map-popup__action" onClick={() => onSelect(selectedPoint.key)}>View details →</button>
                        </div>
                    </OverlayView>}
                </GoogleMap>
        </div>
    );
};

Maps.propTypes = { dataSource: PropTypes.array, selectedKey: PropTypes.string, onSelect: PropTypes.func.isRequired, mapType: PropTypes.string };
export default memo(Maps);
